import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { walletAPI } from '../services/api';
import { TransactionList, ArchiveWalletModal } from '../components/Wallet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

function WalletDetails() {
  const { walletId } = useParams();
  const navigate = useNavigate();

  const [wallet, setWallet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  const [filters, setFilters] = useState({
    type: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [availableWallets, setAvailableWallets] = useState([]);

  useEffect(() => {
    loadWalletData();
    loadAvailableWallets();
  }, [walletId]);

  useEffect(() => {
    loadTransactions();
  }, [walletId, pagination.currentPage, filters]);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [walletResponse, summaryResponse] = await Promise.all([
        walletAPI.getById(walletId),
        walletAPI.getSummary(walletId)
      ]);

      setWallet(walletResponse.data.data);
      setSummary(summaryResponse.data.data);
    } catch (err) {
      console.error('Error loading wallet data:', err);
      setError('Failed to load wallet details');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableWallets = async () => {
    try {
      const response = await walletAPI.getAll({ active: true });
      const wallets = response.data.data?.wallets || [];
      setAvailableWallets(wallets.filter(w => w._id !== walletId));
    } catch (err) {
      console.error('Error loading available wallets:', err);
    }
  };

  const loadTransactions = async () => {
    try {
      setTransactionsLoading(true);

      const options = {
        page: pagination.currentPage,
        limit: 10,
        ...filters
      };

      const response = await walletAPI.getTransactionHistory(walletId, options);
      const data = response.data.data;

      setTransactions(data.transactions || []);
      setPagination({
        currentPage: data.currentPage || 1,
        totalPages: data.totalPages || 1,
        totalItems: data.totalItems || 0
      });
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOwnerTypeIcon = (ownerType) => {
    switch (ownerType) {
      case 'member': return '👤';
      case 'tournament': return '🏌️';
      case 'club': return '🏢';
      case 'contest': return '🏆';
      default: return '💰';
    }
  };

  const getOwnerTypeColor = (ownerType) => {
    switch (ownerType) {
      case 'member': return 'from-blue-500 to-indigo-600';
      case 'tournament': return 'from-purple-500 to-pink-600';
      case 'club': return 'from-emerald-500 to-teal-600';
      case 'contest': return 'from-amber-500 to-orange-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleRefresh = () => {
    loadWalletData();
    loadTransactions();
    loadAvailableWallets();
  };

  const handleArchiveWallet = () => {
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async (transferData) => {
    try {
      setShowArchiveModal(false);
      await loadWalletData();
      navigate('/wallets');
    } catch (err) {
      console.error('Error after archive:', err);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-4 w-32 mb-6" />

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div>
                <Skeleton className="h-8 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mb-8">
            <CardHeader>
              <Skeleton className="h-6 w-40" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i}>
                    <Skeleton className="h-4 w-20 mb-1" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !wallet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/wallets"
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            ← Back to Wallets
          </Link>

          <Alert variant="destructive">
            <AlertTitle>Error Loading Wallet</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>{error || 'Wallet not found'}</span>
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/wallets"
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            ← Back to Wallets
          </Link>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            🔄 Refresh
          </Button>
        </div>

        {/* Wallet Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className={cn(
              "w-16 h-16 rounded-full flex items-center justify-center text-3xl bg-gradient-to-br shadow-lg",
              getOwnerTypeColor(wallet.ownerType)
            )}>
              {getOwnerTypeIcon(wallet.ownerType)}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900">{wallet.ownerName}</h1>
                <Badge
                  variant={wallet.isActive ? "success" : "secondary"}
                  className="capitalize"
                >
                  <span className={cn(
                    "w-1.5 h-1.5 rounded-full mr-1.5",
                    wallet.isActive ? "bg-green-500" : "bg-slate-400"
                  )} />
                  {wallet.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <p className="text-slate-600 capitalize">{wallet.ownerType} Wallet</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
              <Link to={`/wallets/${walletId}/deposit`}>⬇️ Deposit</Link>
            </Button>
            <Button asChild variant="destructive">
              <Link to={`/wallets/${walletId}/withdraw`}>⬆️ Withdraw</Link>
            </Button>
            <Button asChild variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <Link to={`/wallets/transfer?from=${walletId}`}>↔️ Transfer</Link>
            </Button>
            {wallet.isActive && (
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={handleArchiveWallet}
              >
                📦 Archive
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="relative overflow-hidden border-0 shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 opacity-90" />
            <CardContent className="relative p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="text-2xl">💰</div>
                <div>
                  <div className="text-sm text-emerald-100 uppercase tracking-wide">Current Balance</div>
                  <div className="text-2xl font-bold">{formatCurrency(wallet.balance)}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {summary && (
            <>
              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 opacity-90" />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">⬇️</div>
                    <div>
                      <div className="text-sm text-blue-100 uppercase tracking-wide">Total Incoming</div>
                      <div className="text-2xl font-bold">{formatCurrency(summary.summary.totalIncoming)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500 to-red-600 opacity-90" />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">⬆️</div>
                    <div>
                      <div className="text-sm text-rose-100 uppercase tracking-wide">Total Outgoing</div>
                      <div className="text-2xl font-bold">{formatCurrency(summary.summary.totalOutgoing)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border-0 shadow-lg">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 opacity-90" />
                <CardContent className="relative p-6 text-white">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">📊</div>
                    <div>
                      <div className="text-sm text-purple-100 uppercase tracking-wide">Total Transactions</div>
                      <div className="text-2xl font-bold">{summary.summary.transactionCount}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Wallet Information */}
        <Card className="mb-8 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg">Wallet Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Wallet ID</div>
                <div className="text-sm font-mono text-slate-700 bg-slate-100 px-2 py-1 rounded inline-block break-all">
                  {wallet._id}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Created</div>
                <div className="text-sm text-slate-900">{formatDate(wallet.createdAt)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Last Updated</div>
                <div className="text-sm text-slate-900">{formatDate(wallet.updatedAt)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Created By</div>
                <div className="text-sm text-slate-900">{wallet.createdBy || 'System'}</div>
              </div>
              {wallet.notes && (
                <div className="md:col-span-2">
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Notes</div>
                  <div className="text-sm text-slate-700 italic">{wallet.notes}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Transaction History</CardTitle>
              {pagination.totalItems > 0 && (
                <span className="text-sm text-slate-500">{pagination.totalItems} total transactions</span>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <TransactionList
              transactions={transactions}
              loading={transactionsLoading}
              showPagination={true}
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              showFilters={true}
              onFilterChange={handleFilterChange}
            />
          </CardContent>
        </Card>

        {/* Archive Modal */}
        {showArchiveModal && (
          <ArchiveWalletModal
            wallet={wallet}
            availableWallets={availableWallets}
            onClose={() => setShowArchiveModal(false)}
            onArchive={(transferData) => handleArchiveConfirm(transferData)}
          />
        )}
      </div>
    </div>
  );
}

export default WalletDetails;
